/**
 * PhaseSummaryPanel — bottom-right slot content when ≥5 phases are present.
 *
 * Lists the phases that didn't fit into the 3 axial slots (top-left,
 * top-right, bottom-left). Clicking a phase here promotes it to the active
 * slot, the same way the tab bar does. The 3D model takes over this slot
 * during an active annotation; this panel is hidden in that case.
 */
import React from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { activeDiscriminatorAtom, availableDiscriminatorsAtom } from "../../atoms/gatingAtoms";
import { PHASE_LABEL, PHASE_LABEL_SHORT, type Discriminator } from "../../types/gating";

interface Props {
  /**
   * Series UIDs currently occupying the three axial slots. Required — the
   * grid is the authority on which phases are on screen, so this panel
   * derives "hidden" by subtraction rather than guessing based on phase
   * order. (No silent "all but the first 3" fallback: if the grid ever
   * picks a non-default trio of slots, that guess would silently lie.)
   */
  shownSeriesUids: ReadonlySet<string>;
}

export const PhaseSummaryPanel: React.FC<Props> = ({ shownSeriesUids }) => {
  const available = useAtomValue(availableDiscriminatorsAtom);
  const setActive = useSetAtom(activeDiscriminatorAtom);

  const phaseDs = available.filter(
    (d): d is Discriminator & { kind: "phase" } => d.kind === "phase",
  );
  const hidden = phaseDs.filter((d) => !shownSeriesUids.has(d.seriesInstanceUid));

  if (hidden.length === 0) {
    return (
      <div className="w-full h-full bg-bg-2 flex items-center justify-center text-xs text-fg-muted">
        All phases on screen
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-bg-2 p-3 overflow-y-auto">
      <div className="text-[10px] font-semibold text-fg-muted uppercase tracking-wide mb-2">
        Other phases ({hidden.length})
      </div>
      <div className="space-y-1.5">
        {hidden.map((d) => {
          const phase = d.value;
          return (
            <button
              key={d.seriesInstanceUid}
              type="button"
              onClick={() => setActive(d)}
              className="w-full flex items-center justify-between rounded border border-border bg-surface hover:bg-surface-2 px-2 py-1.5 text-left text-fg-muted hover:text-fg transition-colors"
            >
              <span className="text-[11px] font-medium">{PHASE_LABEL[phase]}</span>
              <span className="text-[10px] font-mono text-fg-dim">{PHASE_LABEL_SHORT[phase]}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
